import { submitFeedback } from '../prototypeActions';
import type { PrototypeState } from '../controllerState';
import type { Root } from '../prototypeTypes';
import { resetFeedbackForm, showFeedbackSuccess } from '../formProfileController';
import { showToast } from '../toastController';
import { recordRuntimeError } from '../prototypeRuntimeSupport';
import { byId } from '../domUtils';
import { invalidateFeedbackAttachmentUpload } from '../feedbackAttachmentUploadState';
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';
import {
	activateFeedbackTab,
	invalidateFeedbackContext,
	loadMyFeedback,
	openFeedbackDetail,
	submitFeedbackReply,
	switchToFeedbackTab,
} from './profileFeedbackTicketActions';
import {
	changeAutoRenew,
	closeCancelAutoRenewModal,
	closeUpgradeResumeModal,
	confirmCancelAutoRenew,
	openBillingPortal,
	openCancelAutoRenewModal,
	openFeedbackFromUpgradeResume,
	resumeAutoRenew,
	resumeUpgradeRenew,
	togglePrivacyPanel,
} from './profileBillingActions';
import { closeUpgradeConfirmModal, confirmUpgradeFromPrompt } from '../actions/billingUpgradeConfirmPrompt';
import { closeProfilePasswordModal, closeSetPasswordConfirmModal, handleProfilePasswordAction, submitProfilePasswordChange, submitSetPasswordConfirm } from '../profilePasswordActions';

export async function routeProfileAndFeedbackActions(normalized: string, root: Root, source: HTMLElement, state: PrototypeState): Promise<void> {
	if (normalized.includes('previewPromotionCode')) { const { previewPromotionCode } = await import('../promotionRedeemController'); await previewPromotionCode(root, state); return; }
	if (normalized.includes('redeemPromotionCode')) { const { redeemPromotionCode } = await import('../promotionRedeemController'); await redeemPromotionCode(root, state); return; }
	if (normalized.includes('handleProfilePasswordAction')) return handleProfilePasswordAction(root, state);
	if (normalized.includes('submitProfilePasswordChange')) return submitProfilePasswordChange(root, state);
	if (normalized.includes('closeProfilePasswordModal')) { closeProfilePasswordModal(root); return; }
	if (normalized.includes('submitSetPasswordConfirm')) return submitSetPasswordConfirm(root, state);
	if (normalized.includes('closeSetPasswordConfirmModal')) { closeSetPasswordConfirmModal(root, state.runtime); return; }
	if (normalized.includes('submitFeedback()')) {
		clearOperationFeedback(root, 'feedbackFormAlert');
		await submitFeedback(root, state.runtime, {
			showFeedbackSuccess,
			failValidation: (message) => paintOperationFeedback(root, 'feedbackFormAlert', message),
			notifyPreservedDraftSuccess: () => paintOperationFeedback(root, 'feedbackFormAlert', 'Your previous submission was successful. Your newer edits are still here.', 'success'),
			recordRuntimeError,
			currentPlan: state.effectiveAccessPlan,
			isLoggedIn: state.isLoggedIn,
		});
		return;
	}
	if (normalized.includes('resetFeedbackForm')) {
		resetFeedbackForm(root);
		return;
	}
	if (normalized.includes('updateFeedbackCharCount')) {
		const textarea = byId(root, 'feedbackBody') as HTMLTextAreaElement | null;
		const counter = byId(root, 'feedbackCharCount');
		if (textarea && counter) {
			counter.textContent = `${textarea.value.length} / 2000 characters`;
			counter.style.color = textarea.value.length > 1800 ? 'var(--error)' : 'var(--fg-tertiary)';
		}
		return;
	}
	if (normalized.includes('clearFeedbackFile')) {
		invalidateFeedbackAttachmentUpload(root);
		const preview = root.querySelector<HTMLElement>('#feedbackFilePreview');
		const input = byId(root, 'feedbackFileInput') as HTMLInputElement | null;
		const clearBtn = byId(root, 'feedbackFileClear') as HTMLButtonElement | null;
		const objectKey = preview?.getAttribute('data-uploaded-key');
		if (clearBtn) { clearBtn.disabled = true; clearBtn.style.opacity = '0.5'; }
		if (objectKey) state.runtime.api.deleteAttachment(objectKey).catch((error) => console.warn('[feedback] failed to delete attachment from R2', error));
		if (preview) { preview.style.display = 'none'; preview.removeAttribute('data-uploaded-key'); preview.removeAttribute('data-uploaded-submission-id'); }
		if (input) input.value = '';
		if (clearBtn) { clearBtn.disabled = false; clearBtn.style.opacity = ''; }
		return;
	}
	if (normalized.includes('triggerFeedbackFileUpload')) {
		(byId(root, 'feedbackFileInput') as HTMLInputElement | null)?.click();
		return;
	}
	if (normalized.includes('switchFeedbackTab')) {
		const tab = source.dataset.tab === 'my' ? 'my' : 'send';
		invalidateFeedbackContext(root);
		switchToFeedbackTab(root, tab);
		activateFeedbackTab(root, tab);
		if (tab === 'my') {
			await loadMyFeedback(root, state);
		} else {
			// If returning to Send tab after a successful submission, reset the form
			// so the user can submit again instead of seeing the stale Thank You page.
			const successEl = root.querySelector<HTMLElement>('#feedbackSuccess');
			if (successEl && successEl.style.display !== 'none') {
				resetFeedbackForm(root);
			}
		}
		return;
	}
	if (normalized.includes('switchToMyFeedback')) {
		invalidateFeedbackContext(root);
		switchToFeedbackTab(root, 'my');
		activateFeedbackTab(root, 'my');
		await loadMyFeedback(root, state);
		return;
	}
	if (normalized.includes('openFeedbackNotificationTicket(')) {
		const ticketId = normalized.match(/openFeedbackNotificationTicket\(([^)]+)\)/)?.[1].replace(/['"]/g, '');
		if (!ticketId) return;
		invalidateFeedbackContext(root);
		switchToFeedbackTab(root, 'my');
		activateFeedbackTab(root, 'my');
		if (!await loadMyFeedback(root, state)) return;
		await openFeedbackDetail(root, state, ticketId);
		return;
	}
	if (normalized.includes('switchToSendFeedback')) {
		invalidateFeedbackContext(root);
		switchToFeedbackTab(root, 'send');
		activateFeedbackTab(root, 'send');
		return;
	}
	if (normalized.includes('loadMyFeedback')) { await loadMyFeedback(root, state); return; }
	if (normalized.includes('openFeedbackDetail(')) {
		const ticketId = normalized.match(/openFeedbackDetail\(([^)]+)\)/)?.[1].replace(/['"]/g, '');
		if (ticketId) await openFeedbackDetail(root, state, ticketId);
		return;
	}
	if (normalized.includes('backToFeedbackList')) {
		invalidateFeedbackContext(root);
		const list = root.querySelector<HTMLElement>('#feedbackListView');
		const detail = root.querySelector<HTMLElement>('#feedbackDetailView');
		if (list) {
			list.hidden = false;
			list.style.display = '';
		}
		if (detail) {
			detail.hidden = true;
			detail.style.display = 'none';
		}
		return;
	}
	if (normalized.includes('submitFeedbackReply')) return submitFeedbackReply(root, state);
	if (normalized.includes('openLegalDocument(')) {
		const kind = normalized.match(/openLegalDocument\(['"]([^'"]+)['"]\)/)?.[1];
		if (kind === 'privacy' || kind === 'terms' || kind === 'security' || kind === 'refund-policy') {
			const { openLegalDocument } = await import('../legalDocumentModal');
			await openLegalDocument(root, kind);
		}
		return;
	}
	if (normalized.includes('closeLegalDocument')) {
		const { closeLegalDocument } = await import('../legalDocumentModal');
		closeLegalDocument(root);
		return;
	}
	if (normalized.includes('togglePrivacyPanel')) { togglePrivacyPanel(root); return; }
	if (normalized.includes('switchMembershipBillingTab')) {
		const tab = normalized.includes('renewal') ? 'renewal' : normalized.includes('redemptions') ? 'redemptions' : 'credits';
		const { switchMembershipBillingTab } = await import('../membershipBillingHydrator');
		switchMembershipBillingTab(root, tab);
		return;
	}
	if (normalized.includes('toggleMembershipAutoRenew')) {
		const toggle = root.querySelector<HTMLInputElement>('#membershipRenewalToggle');
		if (toggle?.checked) {
			toggle.checked = false;
			const outcome = await changeAutoRenew(root, state, true);
			if (outcome.ok) {
				toggle.checked = true;
				showToast({ type: 'success', message: 'Auto-renew resumed.' });
			} else {
				showToast({ type: 'error', message: outcome.message });
			}
		} else {
			if (toggle) toggle.checked = true;
			await openCancelAutoRenewModal(root, state);
		}
		return;
	}
	if (normalized.includes('openBillingPortal')) return openBillingPortal(root, state);
	if (normalized.includes('cancelAutoRenew')) return openCancelAutoRenewModal(root, state);
	if (normalized.includes('closeCancelAutoRenewModal')) { closeCancelAutoRenewModal(root); return; }
	if (normalized.includes('confirmCancelAutoRenew')) return confirmCancelAutoRenew(root, state);
	if (normalized.includes('resumeAutoRenew')) {
		if (source instanceof HTMLButtonElement) await resumeAutoRenew(root, state, source);
		return;
	}
	if (normalized.includes('resumeUpgradeRenew')) return resumeUpgradeRenew(root, state);
	if (normalized.includes('closeUpgradeResumeModal')) { closeUpgradeResumeModal(root); return; }
	if (normalized.includes('openFeedbackFromUpgradeResume')) return openFeedbackFromUpgradeResume(root, state);
	if (normalized.includes('confirmUpgradeFromPrompt')) return confirmUpgradeFromPrompt(root);
	if (normalized.includes('closeUpgradeConfirmModal')) { closeUpgradeConfirmModal(root); return; }
	if (normalized.includes('share') && !normalized.includes('shareVfChapter')) showToast({ type: 'success', title: 'Copied', message: 'Share link copied.' });
}
